import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../../constants/common'
import { useContentOverflow } from '../../../../hooks'
import { stopPropagation } from '../../../../utils/events'
import { ActionSelect } from '../../../Action'
import Button, { BUTTON_HEIGHT_24, BUTTON_HEIGHT_32 } from '../../../Button'
import { FloatingTooltip } from '../../../Floating'
import {
  getVerticalIconChevronDirection,
  ICON_COLOR,
  ICON_HEIGHT_16,
  IconChevron,
  IconNG,
} from '../../../Icon'
import Text from '../../../Text'
import {
  QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_ADD_ACTIVITY,
  QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_PHASE_ACTIONS,
  QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_PHASE_EMAILS,
  QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_PHASE_FILES,
  QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_START_PHASE,
} from '../RequestProfileTabActivities.constants'
import {
  getCanViewActivitiesVisibleToDwellers,
  getRequestProfileTabActivitiesPhaseActions,
  getRequestProfileTabActivitiesPhaseCounter,
  isPhaseActionToggleVisibility,
  isRequestProfileTabActivitiesPhasePopulatable,
} from '../RequestProfileTabActivities.utils'

import { RequestProfileTabActivitiesPhaseHeaderMetaItem } from './RequestProfileTabActivitiesPhaseHeaderMetaItem'
import { RequestProfileTabActivitiesPhaseHeaderVisibility } from './RequestProfileTabActivitiesPhaseHeaderVisibility'

import * as styles from './RequestProfileTabActivitiesPhaseHeader.module.css'

import AttachIcon from '../../../../icons/attach.svg'
import EmailIcon from '../../../../icons/email.svg'
import PlusIcon from '../../../../icons/plus.svg'

function RequestProfileTabActivitiesPhaseHeader({
  canEditActivities,
  isOpen,
  isStartPhaseLoading,
  permissions,
  phase,
  onAddActivity,
  onGoToAttachedEmails,
  onGoToAttachedFiles,
  onReloadPhase,
  onStartPhase,
  onToggleVisibility,
}) {
  const { t } = useTranslation()

  const { overflow, setContentOverflowRef } = useContentOverflow()

  const { done, total } = getRequestProfileTabActivitiesPhaseCounter(
    phase.activities
  )

  const isPopulatable = isRequestProfileTabActivitiesPhasePopulatable(phase)

  const isEmpty = isPopulatable || total === 0

  const phaseContextActions =
    permissions?.can_add_activity && !isPopulatable
      ? getRequestProfileTabActivitiesPhaseActions({
          canToggleVisibility: canEditActivities,
          isVisible: phase.visible_to_tenant,
          t,
        })
      : EMPTY_LIST

  function handleClickAddActivity(event) {
    stopPropagation(event)
    onAddActivity?.()
  }

  async function handleClickPhaseContextAction(action) {
    return isPhaseActionToggleVisibility(action?.value)
      ? onToggleVisibility?.()
      : onAddActivity?.()
  }

  function handleClickStartPhase(event) {
    stopPropagation(event)
    onStartPhase?.()
  }

  return (
    <div className={styles.RequestProfileTabActivitiesPhaseHeader}>
      <div className={styles.headerRow}>
        <FloatingTooltip content={phase.name} disabled={!overflow}>
          <Text className={styles.title} ref={setContentOverflowRef}>
            {phase.name}
          </Text>
        </FloatingTooltip>
        {isPopulatable ? null : (
          <IconNG
            Icon={IconChevron}
            className={styles.chevron}
            color={ICON_COLOR.SecondaryTextColor70}
            iconProps={{ direction: getVerticalIconChevronDirection(isOpen) }}
            size={ICON_HEIGHT_16}
          />
        )}
        <div className={styles.meta}>
          <RequestProfileTabActivitiesPhaseHeaderMetaItem>
            {isEmpty
              ? t('RequestProfileTabActivities.ActivitiesCountKey', {
                  count: total,
                })
              : `${done}/${total}`}
          </RequestProfileTabActivitiesPhaseHeaderMetaItem>
          {isEmpty ? null : (
            <>
              <RequestProfileTabActivitiesPhaseHeaderMetaItem
                Icon={EmailIcon}
                interactive={phase.request_thread_count > 0}
                qaName={QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_PHASE_EMAILS}
                tooltipKey={
                  phase.request_thread_count > 0
                    ? 'RequestProfileTabActivities.GoToAttachedEmailsActionKey'
                    : undefined
                }
                onClick={onGoToAttachedEmails}
              >
                {phase.request_thread_count}
              </RequestProfileTabActivitiesPhaseHeaderMetaItem>
              <RequestProfileTabActivitiesPhaseHeaderMetaItem
                Icon={AttachIcon}
                interactive={phase.file_count > 0}
                qaName={QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_PHASE_FILES}
                tooltipKey={
                  phase.file_count > 0
                    ? 'RequestProfileTabActivities.GoToAttachedFilesActionKey'
                    : undefined
                }
                onClick={onGoToAttachedFiles}
              >
                {phase.file_count}
              </RequestProfileTabActivitiesPhaseHeaderMetaItem>
              {getCanViewActivitiesVisibleToDwellers(permissions) ? (
                <RequestProfileTabActivitiesPhaseHeaderVisibility
                  canEdit={canEditActivities}
                  phase={phase}
                  onReloadPhase={onReloadPhase}
                />
              ) : null}
            </>
          )}
          {canEditActivities && isPopulatable ? (
            <Button.Contained
              className={styles.startPhaseButton}
              height={BUTTON_HEIGHT_32}
              isLoading={isStartPhaseLoading}
              qaName={QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_START_PHASE}
              text={t('RequestProfileTabActivities.StartPhaseButtonKey')}
              tooltip={t('RequestProfileTabActivities.StartPhaseTooltipKey')}
              onClick={handleClickStartPhase}
            />
          ) : null}
          {permissions?.can_add_activity && isEmpty && !isPopulatable ? (
            <Button.Outlined
              isGrey
              beforeIconProps={{ Icon: PlusIcon }}
              height={BUTTON_HEIGHT_24}
              qaName={QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_ADD_ACTIVITY}
              tooltip={t('RequestProfileTabActivities.AddActivityTooltipKey')}
              onClick={handleClickAddActivity}
            />
          ) : null}
          {phaseContextActions.length > 0 ? (
            <ActionSelect
              actions={phaseContextActions}
              controlProps={{
                buttonProps: {
                  qaName: QA_NAME_REQUEST_PROFILE_TAB_ACTIVITIES_PHASE_ACTIONS,
                },
              }}
              onClickAction={handleClickPhaseContextAction}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default RequestProfileTabActivitiesPhaseHeader
